import GameModes from "../../enums/GameModes";
import PhaseOfPlay from "../../enums/PhaseOfPlay";
import PlanIntervalUnits from "../../enums/PlanIntervalUnits";
import PlanProgressModels from "../../enums/PlanProgressModels";
import PlanTypes from "../../enums/PlanTypes";
import PlayPositions from "../../enums/PlayPositions";
import PlaySessionTypes from "../../enums/PlaySessionTypes";
import { boolOrIntCodec, nonEmptyStringCodec, timestampCodec } from "../../validation/codecs";
import { enumCodec, mixed, optional, required, t } from "../../validation/validator";
import { CallResponseMixinSchema } from "../common/callResponseSchema";

const nullableString = t.union([t.string, t.null, t.undefined]);
const nullableNumber = t.union([t.number, t.null, t.undefined]);
const nullableTimestamp = t.union([timestampCodec, t.null, t.undefined]);

export const PLAN_WEEKLY_SUGGESTION_SLOT_COUNT = 5;

export const PlanItemRecordSchema = t.intersection([
  t.type({
    id: t.number,
    plan_id: t.number,
    created_at: timestampCodec,
    item_index: t.number,
    game_mode: enumCodec(GameModes),
    is_active: t.boolean,
  }),
  t.partial({
    updated_at: nullableTimestamp,
    title: nullableString,
    description: nullableString,
    session_type: t.union([enumCodec(PlaySessionTypes), t.null, t.undefined]),
    scenario_count: nullableNumber,
    phase: t.union([enumCodec(PhaseOfPlay), t.null, t.undefined]),
    play_position: t.union([enumCodec(PlayPositions), t.null, t.undefined]),
  }),
]);

export type PlanItemRecord = t.TypeOf<typeof PlanItemRecordSchema>;

export const PlanProfileRecordSchema = t.intersection([
  t.type({
    id: t.number,
    created_at: timestampCodec,
    profile_key: t.string,
    name: t.string,
    default_sessions_per_interval: t.number,
    is_active: t.boolean,
  }),
  t.partial({
    updated_at: nullableTimestamp,
    description: nullableString,
  }),
]);

export type PlanProfileRecord = t.TypeOf<typeof PlanProfileRecordSchema>;

export const PlanRecordSchema = t.intersection([
  t.type({
    id: t.number,
    created_at: timestampCodec,
    name: t.string,
    plan_type: enumCodec(PlanTypes),
    progress_model: enumCodec(PlanProgressModels),
    interval_unit: enumCodec(PlanIntervalUnits),
    interval_value: t.number,
    is_active: t.boolean,
    items: t.array(PlanItemRecordSchema),
    profiles: t.array(PlanProfileRecordSchema),
  }),
  t.partial({
    updated_at: nullableTimestamp,
    description: nullableString,
  }),
]);

export type PlanRecord = t.TypeOf<typeof PlanRecordSchema>;

export const UserPlanEnrollmentRecordSchema = t.intersection([
  t.type({
    id: t.number,
    user_id: t.number,
    plan_id: t.number,
    plan_profile_id: t.number,
    selected_sessions_per_interval: t.number,
    interval_unit: enumCodec(PlanIntervalUnits),
    interval_value: t.number,
    week_starts_on: t.number,
    status: t.string,
    current_streak_count: t.number,
    best_streak_count: t.number,
    created_at: timestampCodec,
    started_at: timestampCodec,
    is_active: t.boolean,
  }),
  t.partial({
    timezone: nullableString,
    last_completed_period_start: t.union([timestampCodec, t.string, t.null, t.undefined]),
    updated_at: nullableTimestamp,
    ended_at: nullableTimestamp,
  }),
]);

export type UserPlanEnrollmentRecord = t.TypeOf<typeof UserPlanEnrollmentRecordSchema>;

export const UserPlanPeriodRecordSchema = t.intersection([
  t.type({
    id: t.number,
    user_plan_enrollment_id: t.number,
    period_start_date: t.union([timestampCodec, t.string]),
    required_sessions_count: t.number,
    completed_sessions_count: t.number,
    is_completed: t.boolean,
    created_at: timestampCodec,
  }),
  t.partial({
    completed_at: nullableTimestamp,
    updated_at: nullableTimestamp,
  }),
]);

export type UserPlanPeriodRecord = t.TypeOf<typeof UserPlanPeriodRecordSchema>;

export const UserPlanQualifyingSessionRecordSchema = t.type({
  id: t.number,
  user_plan_enrollment_id: t.number,
  user_plan_period_id: t.number,
  play_session_id: t.number,
  counted_at: timestampCodec,
});

export type UserPlanQualifyingSessionRecord = t.TypeOf<typeof UserPlanQualifyingSessionRecordSchema>;

export const CreatePlanSchema = mixed([
  required({
    name: nonEmptyStringCodec,
  }),
  optional({
    description: t.string,
    plan_type: enumCodec(PlanTypes),
    progress_model: enumCodec(PlanProgressModels),
    interval_unit: enumCodec(PlanIntervalUnits),
    interval_value: t.number,
    profile_ids: t.array(t.number),
    is_active: boolOrIntCodec,
  }),
]);

export type CreatePlanRequest = t.TypeOf<typeof CreatePlanSchema>;

export const GetPlansSchema = optional({
  ids: t.array(t.number),
  include_inactive: boolOrIntCodec,
  plan_type: enumCodec(PlanTypes),
});

export const GetPlanProfilesSchema = optional({
  ids: t.array(t.number),
  include_inactive: boolOrIntCodec,
  profile_key: t.string,
});

export const UpdatePlanSchema = mixed([
  required({
    id: t.number,
  }),
  optional({
    name: nonEmptyStringCodec,
    description: t.union([t.string, t.null]),
    plan_type: enumCodec(PlanTypes),
    progress_model: enumCodec(PlanProgressModels),
    interval_unit: enumCodec(PlanIntervalUnits),
    interval_value: t.number,
    profile_ids: t.array(t.number),
    is_active: boolOrIntCodec,
  }),
]);

export type UpdatePlanRequest = t.TypeOf<typeof UpdatePlanSchema>;

export const DeletePlanSchema = t.type({
  id: t.number,
});

export type DeletePlanRequest = t.TypeOf<typeof DeletePlanSchema>;

export const ApplyActiveTrainingPlansSchema = t.type({
  active_plan_ids: t.array(t.number),
});

export type ApplyActiveTrainingPlansRequest = t.TypeOf<typeof ApplyActiveTrainingPlansSchema>;

export const ApplyActiveTrainingPlansResponseSchema = t.type({
  data: t.array(PlanRecordSchema),
});

export const CreatePlanProfileSchema = mixed([
  required({
    profile_key: nonEmptyStringCodec,
    name: nonEmptyStringCodec,
    default_sessions_per_interval: t.number,
  }),
  optional({
    description: t.string,
    is_active: boolOrIntCodec,
  }),
]);

export type CreatePlanProfileRequest = t.TypeOf<typeof CreatePlanProfileSchema>;

export const UpdatePlanProfileSchema = mixed([
  required({
    id: t.number,
  }),
  optional({
    profile_key: nonEmptyStringCodec,
    name: nonEmptyStringCodec,
    description: t.union([t.string, t.null]),
    default_sessions_per_interval: t.number,
    is_active: boolOrIntCodec,
  }),
]);

export type UpdatePlanProfileRequest = t.TypeOf<typeof UpdatePlanProfileSchema>;

export const DeletePlanProfileSchema = t.type({
  id: t.number,
});

export type DeletePlanProfileRequest = t.TypeOf<typeof DeletePlanProfileSchema>;

export const PlanItemMutationSchema = mixed([
  required({
    item_index: t.number,
  }),
  optional({
    game_mode: enumCodec(GameModes),
    id: nullableNumber,
    title: nullableString,
    description: nullableString,
    session_type: t.union([enumCodec(PlaySessionTypes), t.null]),
    scenario_count: nullableNumber,
    phase: t.union([enumCodec(PhaseOfPlay), t.null]),
    play_position: t.union([enumCodec(PlayPositions), t.null]),
    is_active: boolOrIntCodec,
  }),
]);

export type PlanItemMutation = t.TypeOf<typeof PlanItemMutationSchema>;

export const UpsertPlanItemsSchema = mixed([
  required({
    plan_id: t.number,
    items: t.array(PlanItemMutationSchema),
  }),
  optional({
    delete_item_ids: t.array(t.number),
    replace_existing: boolOrIntCodec,
  }),
]);

export type UpsertPlanItemsRequest = t.TypeOf<typeof UpsertPlanItemsSchema>;

export const StartUserPlanEnrollmentSchema = mixed([
  required({
    plan_id: t.number,
    plan_profile_id: t.number,
    sessions_per_interval: t.number,
  }),
  optional({
    timezone: t.string,
    week_starts_on: t.number,
  }),
]);

export const UpdateUserPlanEnrollmentSchema = mixed([
  required({
    enrollment_id: t.number,
  }),
  optional({
    plan_id: t.number,
    plan_profile_id: t.number,
    sessions_per_interval: t.number,
    timezone: t.string,
    week_starts_on: t.number,
  }),
]);

export const EndUserPlanEnrollmentSchema = t.type({
  enrollment_id: t.number,
});

export const GetUserPlanProgressSchema = optional({
  enrollment_id: nullableNumber,
  include_history: boolOrIntCodec,
});

export const GetUserPlanHistorySchema = optional({
  enrollment_id: nullableNumber,
  limit: t.number,
});

export const UserPlanProgressDataSchema = t.type({
  enrollment: UserPlanEnrollmentRecordSchema,
  plan: PlanRecordSchema,
  current_period: t.union([UserPlanPeriodRecordSchema, t.null]),
  suggested_items: t.array(PlanItemRecordSchema),
  qualifying_sessions: t.array(UserPlanQualifyingSessionRecordSchema),
  history: t.array(UserPlanPeriodRecordSchema),
});

export const UserPlanHistoryDataSchema = t.type({
  enrollments: t.array(UserPlanEnrollmentRecordSchema),
  periods: t.array(UserPlanPeriodRecordSchema),
});

export const CreatePlanResponseSchema = t.type({
  plan_id: t.number,
});

export const AffectedPlanMutationResponseSchema = t.type({
  plan_id: t.number,
});

export const CreatePlanProfileResponseSchema = t.type({
  plan_profile_id: t.number,
});

export const AffectedPlanProfileMutationResponseSchema = t.type({
  plan_profile_id: t.number,
});

export const UpsertPlanItemsResponseSchema = t.type({
  item_ids: t.array(t.number),
});

export const GetAvailableTrainingPlansResponseSchema = mixed([
  required({
    data: t.array(PlanRecordSchema),
  }),
  CallResponseMixinSchema,
]);
export type GetAvailableTrainingPlansResponse = t.TypeOf<typeof GetAvailableTrainingPlansResponseSchema>;

export const GetPlansResponseSchema = t.array(PlanRecordSchema);
export const GetPlanProfilesResponseSchema = t.array(PlanProfileRecordSchema);
export const GetUserPlanProgressResponseSchema = t.union([UserPlanProgressDataSchema, t.null]);
export const GetUserPlanHistoryResponseSchema = UserPlanHistoryDataSchema;
export type GetUserPlanHistoryResponse = t.TypeOf<typeof GetUserPlanHistoryResponseSchema>;
export const UserPlanEnrollmentMutationResponseSchema = t.type({
  enrollment_id: t.number
});
export type UserPlanEnrollmentMutationResponse = t.TypeOf<
  typeof UserPlanEnrollmentMutationResponseSchema
>;

export const TrainingPlanApis = {
  TrainingPlans: {
    CreatePlan: {
      Name: "trainingPlans-createPlan",
      Request: CreatePlanSchema,
      Response: CreatePlanResponseSchema,
    },
    GetPlans: {
      Name: "trainingPlans-getPlans",
      Request: GetPlansSchema,
      Response: GetPlansResponseSchema,
    },
    GetPlanProfiles: {
      Name: "trainingPlans-getPlanProfiles",
      Request: GetPlanProfilesSchema,
      Response: GetPlanProfilesResponseSchema,
    },
    UpdatePlan: {
      Name: "trainingPlans-updatePlan",
      Request: UpdatePlanSchema,
      Response: AffectedPlanMutationResponseSchema,
    },
    DeletePlan: {
      Name: "trainingPlans-deletePlan",
      Request: DeletePlanSchema,
      Response: AffectedPlanMutationResponseSchema,
    },
    ApplyActiveTrainingPlans: {
      Name: "trainingPlans-applyActiveTrainingPlans",
      Request: ApplyActiveTrainingPlansSchema,
      Response: ApplyActiveTrainingPlansResponseSchema,
    },
    CreatePlanProfile: {
      Name: "trainingPlans-createPlanProfile",
      Request: CreatePlanProfileSchema,
      Response: CreatePlanProfileResponseSchema,
    },
    UpdatePlanProfile: {
      Name: "trainingPlans-updatePlanProfile",
      Request: UpdatePlanProfileSchema,
      Response: AffectedPlanProfileMutationResponseSchema,
    },
    DeletePlanProfile: {
      Name: "trainingPlans-deletePlanProfile",
      Request: DeletePlanProfileSchema,
      Response: AffectedPlanProfileMutationResponseSchema,
    },
    UpsertPlanItems: {
      Name: "trainingPlans-upsertPlanItems",
      Request: UpsertPlanItemsSchema,
      Response: UpsertPlanItemsResponseSchema,
    },
    StartUserPlanEnrollment: {
      Name: "trainingPlans-startUserPlanEnrollment",
      Request: StartUserPlanEnrollmentSchema,
      Response: UserPlanEnrollmentMutationResponseSchema,
    },
    UpdateUserPlanEnrollment: {
      Name: "trainingPlans-updateUserPlanEnrollment",
      Request: UpdateUserPlanEnrollmentSchema,
      Response: UserPlanEnrollmentMutationResponseSchema,
    },
    EndUserPlanEnrollment: {
      Name: "trainingPlans-endUserPlanEnrollment",
      Request: EndUserPlanEnrollmentSchema,
      Response: UserPlanEnrollmentMutationResponseSchema,
    },
    GetUserPlanProgress: {
      Name: "trainingPlans-getUserPlanProgress",
      Request: GetUserPlanProgressSchema,
      Response: GetUserPlanProgressResponseSchema,
    },
    GetUserPlanHistory: {
      Name: "trainingPlans-getUserPlanHistory",
      Request: GetUserPlanHistorySchema,
      Response: GetUserPlanHistoryResponseSchema,
    },
  },
};
